import { Link } from "react-router-dom";
import { ArrowUpRight, Phone } from "lucide-react";
import { WhatsAppIcon } from "./ui-bits";
import { PHONE_DISPLAY, PHONE_TEL, WHATSAPP_URL } from "./Navbar";
import { useLang, CHROME, PAGES } from "../i18n/LangContext";

const testid = (label) => `footer-link-${label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`;

const COLS = [
  { key: "products", links: ["/products/pre-construction-finance", "/apply"] },
  { key: "company", links: ["/about", "/book", "/contact", "/expert-network"] },
  { key: "information", links: ["/insights", "/brokers", "/investors", "/brokers/submit", "/faq", "/sitemap"] },
];

const LEGAL = ["/privacy", "/terms", "/cookies", "/lending-disclaimer"];

const LABEL_OVERRIDES = {
  "/products/pre-construction-finance": { en: "Pre-construction", pt: "Pré-construção" },
};

const COPY = {
  en: {
    tagline: "Short-term, senior-secured financing for experienced real estate developers.",
    disclaimer: (
      <>
        Verde Capital is not a bank. Verde Capital is a subsidiary of{" "}
        <a
          href="https://www.verde2050.com"
          target="_blank"
          rel="noopener noreferrer"
          className="underline underline-offset-2 transition-colors hover:text-cream/70"
        >
          Verde 2050
        </a>
        , an independent real estate investment platform. Loans are made by its registered
        company, Verde Partner Capital B.V., and affiliated lending entities. Nothing on this
        website is an offer of credit. All lending is subject to underwriting, security and
        full due diligence.
      </>
    ),
  },
  pt: {
    tagline: "Financiamento de curto prazo com garantia sénior para promotores imobiliários experientes.",
    disclaimer: (
      <>
        A Verde Capital não é um banco. A Verde Capital é uma subsidiária da{" "}
        <a
          href="https://www.verde2050.com"
          target="_blank"
          rel="noopener noreferrer"
          className="underline underline-offset-2 transition-colors hover:text-cream/70"
        >
          Verde 2050
        </a>
        , uma plataforma independente de investimento imobiliário. Os créditos são concedidos
        pela sua empresa registada, Verde Partner Capital B.V., e entidades de crédito
        associadas. Nada neste website constitui uma oferta de crédito. Todo o crédito está
        sujeito a subscrição, garantias e diligência prévia.
      </>
    ),
  },
  nl: {
    tagline: "Kortlopende financiering met senior zekerheid voor ervaren vastgoedontwikkelaars.",
    disclaimer: (
      <>
        Verde Capital is geen bank. Verde Capital is een dochteronderneming van{" "}
        <a
          href="https://www.verde2050.com"
          target="_blank"
          rel="noopener noreferrer"
          className="underline underline-offset-2 transition-colors hover:text-cream/70"
        >
          Verde 2050
        </a>
        , een onafhankelijk vastgoedbeleggingsplatform. Leningen worden verstrekt door de
        geregistreerde vennootschap Verde Partner Capital B.V. en gelieerde kredietverlenende
        entiteiten. Niets op deze website is een kredietaanbod. Alle kredietverlening is
        onder voorbehoud van kredietbeoordeling, zekerheden en volledige due diligence.
      </>
    ),
  },
};

export default function Footer() {
  const { lang, lp } = useLang();
  const c = CHROME[lang];
  const copy = COPY[lang];
  return (
    <footer className="bg-verde-950 text-cream grain relative">
      <div className="container-x pt-20 lg:pt-24 pb-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-14">
          {/* Company block */}
          <div className="lg:col-span-4">
            <Link to={lp("/")} aria-label="Verde Capital home" data-testid="footer-logo-home">
              <img loading="lazy" decoding="async" src="/logo-white.png" alt="Verde Capital" className="h-8 sm:h-9 w-auto" data-testid="footer-logo-white" />
            </Link>
            <p className="mt-5 text-sm leading-relaxed text-cream/60" data-testid="footer-tagline">
              {copy.tagline}
            </p>
            <ul className="mt-6 space-y-1 text-sm">
              <li>
                <a href={PHONE_TEL} data-testid="footer-phone" className="inline-flex items-center gap-2.5 py-1.5 text-cream/75 transition-colors hover:text-white">
                  <Phone className="h-4 w-4 text-mint" aria-hidden="true" /> {PHONE_DISPLAY}
                </a>
              </li>
              <li>
                <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer" data-testid="footer-whatsapp" className="inline-flex items-center gap-2.5 py-1.5 text-cream/75 transition-colors hover:text-white">
                  <WhatsAppIcon className="h-4 w-4 text-mint" aria-hidden="true" /> WhatsApp
                </a>
              </li>
              <li>
                <a
                  href="https://www.linkedin.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="footer-linkedin"
                  className="inline-flex items-center gap-1.5 py-1.5 text-cream/75 transition-colors hover:text-white"
                >
                  LinkedIn <ArrowUpRight className="h-3.5 w-3.5" />
                </a>
              </li>
            </ul>
          </div>

          {/* Link columns */}
          <div className="lg:col-span-8 grid grid-cols-2 sm:grid-cols-3 gap-x-8 gap-y-12">
            {COLS.map((col) => (
              <div key={col.key}>
                <h3 className="font-mono text-[11px] font-bold uppercase tracking-[0.22em] text-mint/80">{c[col.key]}</h3>
                <ul className="mt-6 space-y-3">
                  {col.links.map((to) => (
                    <li key={to}>
                      <Link
                        to={lp(to)}
                        data-testid={testid(PAGES[to].en)}
                        className="inline-block py-1.5 text-sm text-cream/60 transition-colors hover:text-white"
                      >
                        {LABEL_OVERRIDES[to]?.[lang] || PAGES[to][lang]}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>

        <div className="mt-20 flex flex-col-reverse lg:flex-row lg:items-end justify-between gap-8 border-t border-cream/10 pt-8">
          <p className="text-xs text-cream/45">© {new Date().getFullYear()} Verde Capital. {lang === "pt" ? "Todos os direitos reservados." : lang === "nl" ? "Alle rechten voorbehouden." : "All rights reserved."}</p>
          <p className="max-w-xl text-[11px] leading-relaxed text-cream/40" data-testid="footer-disclaimer">
            {copy.disclaimer}
          </p>
          <div className="flex flex-wrap items-center gap-x-6 gap-y-3">
            {LEGAL.map((to) => (
              <Link key={to} to={lp(to)} data-testid={testid(PAGES[to].en)} className="inline-block py-1 text-xs text-cream/50 transition-colors hover:text-white">
                {PAGES[to][lang]}
              </Link>
            ))}
          </div>
        </div>
      </div>
    </footer>
  );
}
